import { useState } from 'react';
import axios from 'axios';
import { usePathData } from 'dataProvider/pathData';
import { useAuth } from 'dataProvider/authData';
import calcAPIBase from 'utils/calcAPIBase';
import { extractApiErrorMessage } from 'utils/extractApiErrorMessage';
import isFunction from 'lodash/isFunction';

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

// /**
//  * API呼び出しの状態
//  * + `PENDING`: 呼び出し中
//  * + `FULFILLED: 呼び出し成功
//  * + `REJECTED: 呼び出し失敗（エラー発生）
//  */
export const ASYNC_STATE = {
    PENDING: 'pending',
    FULFILLED: 'fulfilled',
    REJECTED: 'rejected',
    WAITING: 'waiting',
};

/**
 * postリクエストを表すオブジェクトを生成する
 * @param {object} pageData ページ内のフォームなどのデータ。一度そのページを離れ、再度訪れた際に、データを復元するために利用する
 * @param {object} requestData Postデータ。
 * @param {string} state 状態。post呼び出しの状態。`ASYNC_STATE.**`が入る。
 * @param {string} responseData 応答データ。
 * @param {string} errMessage post呼び出しでエラーが発生した場合のエラーメッセージ。
 */
const postResBuilder = (pageData, requestData, state, responseData, errMessage, errors) => ({
    errors,
    pageData,
    requestData,
    state,
    responseData,
    loading: state === ASYNC_STATE.PENDING,
    error: state === ASYNC_STATE.REJECTED,
    waiting: state === ASYNC_STATE.WAITING,
    success: state === ASYNC_STATE.FULFILLED,
    errorMessage: state === ASYNC_STATE.REJECTED ? errMessage ?? 'no error' : 'no error',
});

/**
 * POSTリクエストを行い、リクエストの状態を管理するカスタムフック
 * @return [post, states]
 * + `post`はPOSTリクエストを行う関数。第1引数に`key`、第2引数にパス、第3引数にPOSTするデータを入れて呼び出す。返り値はPromise。
 * + `states`はPOSTリクエストの状態を得る関数。post関数で指定した`key`を与えると、その呼び出しの状態を得ることができる。
 *
 * @example
 * function APICallTest(){
 * 	 const [request, states] = useAPIPost()
 *
 *   const handleClick = () => {
 *     request("post")("sampleKey", "/system/clients")
 *   }
 *
 *   return (
 *     <div>
 *       <button onClick={handleClick}>送信</button>
 *       <p>{states("sampleKey").state}</p> ← API呼び出しの状態が表示される
 *       <p>{states("sampleKey").errorMessage}</p> ← APIリクエストがrejectされた場合にはエラーメッセージが格納されている。それ以外では`no error`と言う文字列が返る
 *     </div>
 *   )
 *
 */
const useAPIPostStateManager = () => {
    const [states, setStates] = useState({});
    const { clientId, userType } = usePathData();
    const { handle401, getToken } = useAuth();

    const apiBase = calcAPIBase(userType, clientId);

    const request = method =>
        /**
         * Post/Put/Deleteリクエストを行う関数を返す関数。
         * @param {string} key PostKey。アプリケーション内で一意な値を指定する。
         * @param {string} string post、put、deleteのいずれかを指定する。
         * @param {string} path apiのエンドポイント。`http://..../api`より後のパスを指定する。(ex `/system/clients`)
         * @param {object} data ポストするデータ
         * @param {object} dataModefier ポストするデータを整形する関数。引数として`data`が与えられ、APIのリクエストに対応した形に整形させる。
         */
        (key, path, data = null, dataModefier = o => o) => {
            if (states[key]?.loading) console.error('Another api call is pending. key: ' + key);
            const requestData = isFunction(dataModefier) ? dataModefier(data) : data;
            setStates(states => ({
                ...states,
                [key]: postResBuilder(data, requestData, ASYNC_STATE.PENDING),
            }));
            const token = getToken();

            const axiosConfig = {
                method: method.toLowerCase(),
                url: apiBase + path,
                data: requestData,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            };

            // Auto-retry CSV import when the server is busy (429).
            // We intentionally scope this to a single endpoint/key to avoid unexpected
            // retries on normal API errors.
            const shouldRetry429 = key === 'admin.user.import.csv' && path === '/users/import-csv';
            const retryWindowMs = 3 * 60 * 1000;
            const baseDelayMs = 1000;
            const maxDelayMs = 10000;

            const requestWithRetry = async () => {
                const startedAt = Date.now();
                let attempt = 0;
                // eslint-disable-next-line no-constant-condition
                while (true) {
                    try {
                        return await axios(axiosConfig);
                    } catch (err) {
                        const status = err?.response?.status;
                        if (!shouldRetry429 || status !== 429) {
                            throw err;
                        }

                        const elapsed = Date.now() - startedAt;
                        const remainingWindowMs = retryWindowMs - elapsed;
                        if (remainingWindowMs <= 0) {
                            throw err;
                        }

                        // Prefer Retry-After header if provided.
                        const retryAfterHeader = err?.response?.headers?.['retry-after'];
                        let delayMs = null;
                        if (retryAfterHeader) {
                            const seconds = parseInt(retryAfterHeader, 10);
                            if (!Number.isNaN(seconds) && seconds > 0) {
                                const jitterMs = Math.floor(Math.random() * 1000); // 0..999ms
                                delayMs = seconds * 1000 + jitterMs;
                            }
                        }

                        if (delayMs === null) {
                            const exp = Math.min(maxDelayMs, baseDelayMs * Math.pow(2, attempt));
                            // jitter: +/- 25%
                            const jitter = exp * (0.75 + Math.random() * 0.5);
                            delayMs = Math.floor(jitter);
                        }

                        delayMs = Math.max(0, Math.min(delayMs, remainingWindowMs));

                        attempt++;
                        await sleep(delayMs);
                    }
                }
            };

            return Promise.resolve()
                .then(() => (shouldRetry429 ? requestWithRetry() : axios(axiosConfig)))
                .then(res => {
                    // Set State
                    setStates(states => ({
                        ...states,
                        [key]: postResBuilder(
                            data,
                            requestData,
                            ASYNC_STATE.FULFILLED,
                            res.data.data
                        ),
                    }));
                    // Clear State
                    setStates(states => ({ ...states, [key]: null }));

                    return res;
                })
                .catch(err => {
                    if (err.response?.status == 401 && !key.includes('refreshToken')) {
                        handle401();
                    }
                    const errorMessage =
                        shouldRetry429 && err.response?.status === 429
                            ? 'サーバーが混雑しています。少し時間をおいて再度お試しください。'
                            : extractApiErrorMessage(err, {
                                  fallback: '処理に失敗しました',
                              });
                    setStates(states => ({
                        ...states,
                        [key]: postResBuilder(
                            data,
                            requestData,
                            ASYNC_STATE.REJECTED,
                            null,
                            errorMessage,
                            err.response?.data?.errors
                        ),
                    }));
                    throw err;
                });
        };

    return [request, key => states[key] ?? postResBuilder(null, null, ASYNC_STATE.WAITING)];
};

export default useAPIPostStateManager;
